<script lang="ts">
    // TODO: add docs and test in XH-64726
    import cn from 'classnames';
    import { slide } from 'svelte/transition';

    import type { DataAttrs } from '@uikit/types';
    import { Font, FontTypes } from '@uikit/font';
    import { Input, InputTypes } from '@uikit/form/input';
    import { IconName } from '@uikit/svg-icon';
    import { SpinnerCircle } from '@uikit/spinner-circle';

    import SelectOption from './components/select-option/select-option.svelte';
    import type {
        InputHandlerFn,
        ChangeHandlerFn,
        Option,
        Size,
        OptionClickHandler,
        FocusHandlerFn,
        OptionsGroupList,
    } from './types';
    import s from './select.module.css';
    import { getOptionByValue, getOptionGroups, hasOptions as hasOptionsFn, mappedSelectOptions } from './functions';

    export let label: NullableString = undefined;
    export let name: NullableString = undefined;
    export let caption: NullableString = undefined;
    export let disabled: boolean = false;
    export let optionsGroups: OptionsGroupList = [];
    /**
     * @deprecated use 'optionsGroups' instead
     * */
    export let options: Option[] = [];
    export let value: NullableType<Option> = undefined;
    export let placeholder: NullableString = undefined;
    export let isLoading: boolean = false;
    export let loadingText: NullableString = undefined;
    export let required: boolean = false;
    export let classes: NullableString = undefined;
    export let inputContainerClasses: NullableString = undefined;
    export let inputClasses: NullableString = undefined;
    export let optionsListClasses: NullableString = undefined;
    export let captionOnNextLine: boolean = false;
    export let hasHiddenInput: boolean = false;
    export let icon: NullableType<InputTypes.IconData> = undefined;
    export let state: NullableType<InputTypes.State> = undefined;
    export let message: NullableString = undefined;
    export let maxlength: NullableNumber = undefined;
    export let counter: boolean = false;
    export let inputHandler: NullableType<InputHandlerFn> = undefined;
    export let changeHandler: NullableType<ChangeHandlerFn> = undefined;
    export let focusHandler: NullableType<FocusHandlerFn> = undefined;
    export let attrs: Partial<DataAttrs> = {};
    export let attrsOptions: Partial<DataAttrs> = {};
    export let size: NullableType<Size> = undefined;
    export let description: NullableString = undefined;
    export let descriptionLight: boolean = false;
    export let extraInfo: NullableString = undefined;
    export let extraInfoClasses: NullableString = undefined;
    export let withSearchIcon: boolean = true;
    export let isLabelImg: boolean = false;
    export let dropdown: boolean = true;
    export let shouldCloseDropdownOnSelect: boolean = true;

    let isOpen = false;
    let selectContainer: HTMLElement;

    $: shownValue = value ? value.label : '';
    $: groups = getOptionGroups(optionsGroups, options);
    $: hasOptions = hasOptionsFn(groups);
    $: isNotFoundSlotShown = shownValue.length && !hasOptions && $$slots.notFound;
    $: isDropdownShown = isOpen && Boolean(isLoading || hasOptions || isNotFoundSlotShown || extraInfo);

    export function open() {
        isOpen = true;
    }

    export function close() {
        isOpen = false;
    }

    export function toggle() {
        if (isOpen) {
            close();
        } else {
            open();
        }
    }

    export function clean() {
        shownValue = '';
        value = null;
    }

    function handleInput(e: Event) {
        const { value: inputValue } = e.target as HTMLInputElement;

        if (inputHandler) {
            inputHandler(inputValue);
            open();
        }
    }

    function selectItem(selectedValue: NullableString): void {
        if (!hasOptions || !selectedValue) return;

        const selectedOption = getOptionByValue(groups, selectedValue);

        if (shouldCloseDropdownOnSelect) {
            value = selectedOption;
            close();
        }
        if (changeHandler && selectedOption) {
            changeHandler(selectedOption);
        }
    }

    const optionClickHandlerWrapper = (handler: OptionClickHandler | undefined) => (value: NullableString) => {
        if (!handler || handler(value, shownValue)) {
            selectItem(value);
        } else {
            close();
        }
    };

    const windowClickHandler = (e: MouseEvent) => {
        if (!e.composedPath().includes(selectContainer)) {
            close();
        }
    };
</script>

<svelte:window on:click={windowClickHandler} />

<div bind:this={selectContainer} class={cn(s.container, classes)} {...attrs}>
    <Input
        name={name && !hasHiddenInput ? name : ''}
        {label}
        {caption}
        {disabled}
        {required}
        {size}
        bind:value={shownValue}
        {placeholder}
        readonly={!inputHandler}
        iconLeft={inputHandler && withSearchIcon ? IconName.SearchCompact : null}
        iconRight={icon}
        fullWidth
        dropdown={dropdown && icon === undefined}
        isDropdownOpened={isOpen}
        classes={cn(
            s.pointer,
            {
                [s.readonly]: !inputHandler,
                [s.disabled]: disabled,
            },
            inputContainerClasses,
        )}
        {inputClasses}
        {captionOnNextLine}
        {state}
        {message}
        {maxlength}
        {counter}
        {description}
        {descriptionLight}
        on:input={handleInput}
        on:click={disabled ? () => {} : toggle}
        on:focus={focusHandler}
    >
        <slot name="before-input" slot="before-input" />
        <svelte:fragment slot="hidden-input">
            {#if hasHiddenInput}
                <input type="hidden" name={name ?? ''} value={value?.value ?? ''} />
            {/if}
        </svelte:fragment>
    </Input>
    {#if isDropdownShown}
        <ul class={cn(s.options, optionsListClasses)} transition:slide>
            {#if isLoading}
                <div class={s.spinner}>
                    <SpinnerCircle />
                    {#if loadingText}
                        <Font color={FontTypes.Color.Secondary} type={FontTypes.Type.body}>
                            {loadingText}
                        </Font>
                    {/if}
                </div>
            {/if}

            {#if extraInfo}
                <div class={cn(extraInfoClasses)}>{@html extraInfo}</div>
            {/if}

            {#if !isLoading && hasOptions}
                {#each groups as { name, options }, groupId}
                    {#if options.length}
                        {#if groupId}
                            <hr class={s.divider} />
                        {/if}
                        {#if name}
                            <Font color={FontTypes.Color.Secondary} type={FontTypes.Type.body} classes={s.groupName}>
                                {name}
                            </Font>
                        {/if}
                        {#each options as option, id}
                            <SelectOption
                                {...option}
                                {isLabelImg}
                                attrs={mappedSelectOptions(attrsOptions, `${groupId}-${id}`)}
                                clickHandler={optionClickHandlerWrapper(option.clickHandler)}
                            />
                        {/each}
                    {/if}
                {/each}
            {/if}
            {#if !isLoading && isNotFoundSlotShown}
                <slot name="notFound" value={shownValue} />
            {/if}
        </ul>
    {/if}
</div>
